/**
 * The job-search timeline: "when do you expect to be interviewing?" asked as
 * three stretches painted over one line of months rather than picked from a
 * list of vague windows.
 *
 * All of the rules and all of the copy live here rather than in the
 * component, because both are the feature. A fence that lets through a plan
 * where the offer lands before the interviews is a content bug no render test
 * would catch, and a refusal that says "invalid" instead of naming the
 * earliest legal month is a dead end.
 *
 * Two coordinate systems meet here, deliberately:
 *
 *   - **Month keys** (`'2026-09'`) are what gets STORED, and
 *     what the shared validation reasons about. They mean the same thing
 *     forever, so an application read six months later still says September
 *     2026 rather than sliding along with the calendar.
 *   - **Line indices** are what the widget draws and taps. They only mean
 *     anything next to a particular `timelineMonths(today)` line.
 *
 * The fences are written once, over month ORDER — a plain count of months —
 * so the client's tap check and the server's stored-value check cannot drift
 * apart even though one works in indices and the other in keys.
 */

import { z } from 'zod'
import { assertNever } from '../assert-never.js'
import type { WidgetDescriptor } from './widgets.js'

/** The shortest program we run, counting the first prep month itself. */
export const MIN_PROGRAM_MONTHS = 2

/**
 * How many months the line shows, and so how far out a plan can reach.
 *
 * There is no open-ended cell after the last one. A search more than a year
 * out is not one the program takes on, so the line gives it nowhere to go.
 */
export const MONTHS_ON_LINE = 12

/** The three stretches, in the order the checklist asks for them. */
export const TIMELINE_STRETCHES = ['practice', 'interviewing', 'jobBy'] as const

export type TimelineStretch = (typeof TIMELINE_STRETCHES)[number]

/** What each stretch is called, wherever it has to be named. */
export const STRETCH_LABELS: Record<TimelineStretch, string> = {
  practice: 'Interview prep',
  interviewing: 'Interviewing seriously',
  jobBy: 'Job offer window',
}

/** A month number as a month key writes it: always two digits. */
const MONTH_NUMBERS = [
  '01',
  '02',
  '03',
  '04',
  '05',
  '06',
  '07',
  '08',
  '09',
  '10',
  '11',
  '12',
] as const

/**
 * A month as it is stored: `'2026-09'`.
 *
 * A type as well as a pattern, so a month the calendar does not have is a
 * compile error wherever a stretch is written out by hand, rather than a
 * stretch that reads as nothing once it is stored. The year half is only
 * `${number}`; {@link isMonthKey} is the whole rule, and the only way an
 * unchecked string becomes one.
 */
export type MonthKey = `${number}-${(typeof MONTH_NUMBERS)[number]}`

/** One month on the line, with the three ways it needs to be written. */
export interface TimelineMonth {
  /** Stored form: `'2026-09'`. */
  key: MonthKey
  /** On the line under the month's column: `'Sep'`. */
  label: string
  /** The small line under that — `'Now'`, a year at the turn, or nothing. */
  sub: string
  /** In a sentence: `'September'`, `'January 2027'`. */
  name: string
}

/** A marked stretch, as two line indices. Widget-facing. */
export interface TimelineSpan {
  from: number
  to: number
}

/** A marked stretch, as two month keys. Storage-facing. */
export interface StoredSpan {
  from: MonthKey
  to: MonthKey
}

export type TimelineMarks = Partial<Record<TimelineStretch, TimelineSpan>>

/** What lands in the answers bag under `interviewTimeline`. */
export type TimelineValue = Partial<Record<TimelineStretch, StoredSpan>>

/**
 * The widget's whole interaction state. `marks` is the answer; the other
 * three are the half-finished tap, the stretch being painted, and the last
 * thing we refused, none of which is ever stored.
 */
export interface TimelineState {
  stretch: TimelineStretch
  /** The first month of the stretch in hand, waiting for its second tap. */
  pending: number | null
  marks: TimelineMarks
  refusal: string | null
}

const SHORT_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

const LONG_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

const NUMBER_WORDS = [
  'zero',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
]

/** Small numbers read as words mid-sentence; anything larger falls back. */
function inWords(n: number): string {
  return NUMBER_WORDS[n] ?? String(n)
}

/**
 * The line: this month and the eleven after it.
 *
 * `today` is a parameter rather than a `new Date()` inside, so the board a
 * test asserts against is the same board every time.
 */
export function timelineMonths(today: Date): TimelineMonth[] {
  const thisYear = today.getFullYear()
  const months: TimelineMonth[] = []
  let year = thisYear
  let month = today.getMonth()

  for (let i = 0; i < MONTHS_ON_LINE; i++) {
    months.push({
      key: `${year}-${MONTH_NUMBERS[month]}`,
      label: SHORT_NAMES[month],
      // "Now" anchors the left end; after that the year only appears where
      // it changes, so the line reads as a calendar rather than a table.
      sub: i === 0 ? 'Now' : month === 0 ? String(year) : '',
      name: LONG_NAMES[month] + (year === thisYear ? '' : ` ${year}`),
      // Safe: pushed and never reordered, so the last entry is the one built
      // in this iteration.
    })
    month++
    if (month === 12) {
      month = 0
      year++
    }
  }
  return months
}

/** A month key as a count of months, so the fences are subtraction. */
export function monthOrder(key: MonthKey): number {
  const [year, month] = key.split('-')
  return Number(year) * 12 + (Number(month) - 1)
}

/** `'YYYY-MM'` with a real month number in it. */
const MONTH_KEY = /^\d{4}-(0[1-9]|1[0-2])$/

/**
 * Whether a string is something {@link monthOrder} can place.
 *
 * The guard on the stored shape: without it a key like `'banana'` orders as
 * `NaN`, every fence comparison against it is false, and an impossible plan
 * validates clean.
 */
export function isMonthKey(key: string): key is MonthKey {
  return MONTH_KEY.test(key)
}

/**
 * A stored month key in a sentence, always with its year.
 *
 * The line can drop the current year because the line itself says where you
 * are. A reviewer reading a stored answer has no such context, so this one
 * never abbreviates.
 */
export function monthName(key: MonthKey): string {
  const [year, month] = key.split('-')
  return `${LONG_NAMES[Number(month) - 1]} ${year}`
}

/** The marks as month orders, which is what every fence below reasons about. */
type OrderSpan = { from: number; to: number }
type OrderMarks = Partial<Record<TimelineStretch, OrderSpan>>

/** One reason a month is fenced off, and how to say so if it is the binding one. */
interface Bound {
  order: number
  problem: string
}

/**
 * Everything holding back the FIRST month of a stretch.
 *
 * Overlap between stretches is allowed everywhere it makes sense — real
 * searches prep, interview and wait on offers all at once — so only the
 * nonsense is fenced: interviewing before prep begins, or an offer landing
 * sooner than the shortest program we run could deliver one.
 */
function startBounds(stretch: TimelineStretch, marks: OrderMarks): Bound[] {
  const { practice, interviewing } = marks
  if (stretch === 'interviewing')
    return practice
      ? [
          {
            order: practice.from,
            problem: `${STRETCH_LABELS.interviewing} cannot start before ${STRETCH_LABELS.practice.toLowerCase()} does.`,
          },
        ]
      : []
  if (stretch === 'jobBy') {
    const bounds: Bound[] = []
    if (practice)
      bounds.push({
        order: practice.from + MIN_PROGRAM_MONTHS - 1,
        problem: `The job offer window cannot open before month ${inWords(MIN_PROGRAM_MONTHS)} of prep — the shortest program we run, counting the first prep month.`,
      })
    if (interviewing)
      bounds.push({
        order: interviewing.from,
        problem: 'The job offer window cannot open before interviewing starts.',
      })
    return bounds
  }
  // Prep is the one stretch with nothing in front of it. Said out loud, so a
  // fourth stretch added to TIMELINE_STRETCHES is a compile error here rather
  // than a silent inheritance of "no fences at all" — on the function the
  // server uses to reject a crafted payload.
  if (stretch === 'practice') return []
  return assertNever(stretch, 'Unexpected timeline stretch')
}

/** Everything holding back the LAST month of a stretch, given its first. */
function endBounds(
  stretch: TimelineStretch,
  marks: OrderMarks,
  from: number,
): Bound[] {
  const bounds: Bound[] = [
    {
      order: from,
      problem: `${STRETCH_LABELS[stretch]} cannot end before it starts.`,
    },
  ]
  // The offer window is the hard date. It has to stay open at least as long
  // as they expect to still be interviewing.
  if (stretch === 'jobBy' && marks.interviewing)
    bounds.push({
      order: marks.interviewing.to,
      problem: 'The job offer window cannot close before interviewing ends.',
    })
  return bounds
}

function floorOf(bounds: Bound[]): number {
  return bounds.reduce(
    (max, b) => Math.max(max, b.order),
    Number.NEGATIVE_INFINITY,
  )
}

/** The binding bound when `order` is too early, or null when it is legal. */
function bindingProblem(bounds: Bound[], order: number): string | null {
  const broken = bounds.filter((b) => order < b.order)
  if (broken.length === 0) return null
  return broken.reduce((worst, b) => (b.order > worst.order ? b : worst))
    .problem
}

/** The marks read as month orders, so the shared fences apply to them. */
function ordersFromMarks(
  marks: TimelineMarks,
  months: TimelineMonth[],
): OrderMarks {
  const orders: OrderMarks = {}
  for (const stretch of TIMELINE_STRETCHES) {
    const span = marks[stretch]
    if (!span) continue
    orders[stretch] = {
      from: monthOrder(months[span.from].key),
      to: monthOrder(months[span.to].key),
    }
  }
  return orders
}

/** The first index on `months` at or after `order`, or 0 when unbounded. */
function indexAtOrAfter(months: TimelineMonth[], order: number): number {
  if (order === Number.NEGATIVE_INFINITY) return 0
  const found = months.findIndex((m) => monthOrder(m.key) >= order)
  return found === -1 ? months.length - 1 : found
}

/**
 * The earliest month a tap may land on: the start floor while no month is in
 * hand, the end floor once one is.
 */
export function stretchFloorIndex(
  stretch: TimelineStretch,
  marks: TimelineMarks,
  months: TimelineMonth[],
  pending: number | null,
): number {
  const orders = ordersFromMarks(marks, months)
  if (pending === null)
    return indexAtOrAfter(months, floorOf(startBounds(stretch, orders)))
  const from = monthOrder(months[pending].key)
  return indexAtOrAfter(months, floorOf(endBounds(stretch, orders, from)))
}

/**
 * The latest month a tap may land on.
 *
 * Prep is the only stretch with a ceiling, and it is a property of the LINE
 * rather than of the plan: starting prep in the last month leaves no room to
 * finish the shortest program we run, so there would be nothing to mark next.
 */
export function stretchCeilIndex(
  stretch: TimelineStretch,
  months: TimelineMonth[],
  pending: number | null,
): number {
  if (pending !== null || stretch !== 'practice') return months.length - 1
  return months.length - MIN_PROGRAM_MONTHS
}

/** A fresh board: nothing marked, asking for prep. */
export function emptyTimelineState(): TimelineState {
  return { stretch: 'practice', pending: null, marks: {}, refusal: null }
}

/**
 * Why a tap outside the fences was refused, as a full sentence naming the
 * month they should reach for instead.
 *
 * "That is not allowed" is a dead end on a widget where the rule is not
 * visible. Every one of these says the rule and then the way out.
 */
function refusalFor(
  stretch: TimelineStretch,
  marks: TimelineMarks,
  months: TimelineMonth[],
  pending: number | null,
): string {
  if (pending !== null) {
    const interviewing = marks.interviewing
    if (
      stretch === 'jobBy' &&
      interviewing &&
      pending <= interviewing.to &&
      stretchFloorIndex(stretch, marks, months, pending) > pending
    )
      return `Interviewing runs to ${months[interviewing.to].name}, so the job offer window has to reach at least that far.`
    return `The last month comes after the first. Tap ${months[pending].name} or later to finish the ${STRETCH_LABELS[stretch].toLowerCase()} stretch.`
  }
  if (stretch === 'practice')
    return `The whole plan has to fit within the next ${months.length} months, so prep has to begin by ${months[stretchCeilIndex(stretch, months, null)].name} to leave room for the shortest program we run, which is ${inWords(MIN_PROGRAM_MONTHS)} months.`
  const earliest = months[stretchFloorIndex(stretch, marks, months, null)].name
  if (stretch === 'jobBy')
    return `The program runs at least ${inWords(MIN_PROGRAM_MONTHS)} months counting your first prep month, and an offer cannot land before you start interviewing. The earliest here is ${earliest}. Most people get the best results within three months.`
  return `Interviewing cannot start before your prep does. The earliest here is ${earliest}.`
}

/**
 * One tap on the line.
 *
 * The whole widget is this function plus a drawing. Two taps mark a stretch:
 * the first is held as `pending`, the second closes it. An out-of-fence tap
 * changes nothing except the refusal shown.
 */
export function tapMonth(
  state: TimelineState,
  months: TimelineMonth[],
  index: number,
): TimelineState {
  const { stretch, marks, pending } = state

  if (pending === null) {
    const floor = stretchFloorIndex(stretch, marks, months, null)
    const ceil = stretchCeilIndex(stretch, months, null)
    if (index < floor || index > ceil)
      return { ...state, refusal: refusalFor(stretch, marks, months, null) }
    return { ...state, pending: index, refusal: null }
  }

  if (index < stretchFloorIndex(stretch, marks, months, pending))
    return { ...state, refusal: refusalFor(stretch, marks, months, pending) }

  const next: TimelineMarks = {
    ...marks,
    [stretch]: { from: pending, to: index },
  }
  // A stretch re-marked earlier in the checklist can orphan the ones after
  // it. Those are cleared and asked for again, never silently dragged along
  // into a shape the applicant never chose.
  for (const later of TIMELINE_STRETCHES.slice(
    TIMELINE_STRETCHES.indexOf(stretch) + 1,
  )) {
    const span = next[later]
    if (!span) continue
    if (
      span.from < stretchFloorIndex(later, next, months, null) ||
      span.to < stretchFloorIndex(later, next, months, span.from)
    )
      delete next[later]
  }

  const unmarked = TIMELINE_STRETCHES.filter((k) => !next[k])
  return {
    stretch: unmarked[0] ?? stretch,
    pending: null,
    marks: next,
    refusal: null,
  }
}

/** A marked stretch in a sentence: "September", or "September to November". */
function span(months: TimelineMonth[], s: TimelineSpan): string {
  return s.from === s.to
    ? months[s.from].name
    : `${months[s.from].name} to ${months[s.to].name}`
}

/**
 * What the second tap of the gesture is for, once the first has landed.
 *
 * This takes priority over everything else the board could say. A finished
 * plan that is being re-drawn would otherwise keep reading back its old
 * summary while a tap is in hand, and the applicant would get no sign that
 * the next tap is about to replace a stretch.
 */
const PENDING_ASK: Record<TimelineStretch, (month: string) => string> = {
  practice: (m) =>
    `Prep starts ${m}. Now tap the last month you would still call mostly prep. It can be the same one.`,
  interviewing: (m) =>
    `Interviewing starts ${m}. Now tap the last month. It can be the same one.`,
  jobBy: (m) =>
    `You would love an offer by ${m}. Now tap the month you need one by. It can be the same one.`,
}

/**
 * What a fresh tap on the stretch in hand will draw. Two versions, because
 * "mark this one" and "replace what you already marked" are different
 * promises, and the picker means the second is always reachable.
 */
const OPENING_ASK: Record<TimelineStretch, string> = {
  practice: 'Start with prep: tap the first month, then the last.',
  interviewing:
    'Now mark when you see yourself interviewing seriously: tap the first month, then the last.',
  jobBy:
    'Now mark the offer window: the month you would love an offer by, then the month you need one by.',
}

const REDRAW_ASK: Record<TimelineStretch, string> = {
  practice: 'Tap a month to re-draw prep: the first month, then the last.',
  interviewing:
    'Tap a month to re-draw interviewing: the first month, then the last.',
  jobBy:
    'Tap a month to re-draw the offer window: the month you would love an offer by, then the month you need one by.',
}

/** What has been marked so far, in the order the checklist asks for it. */
function progressLines(
  marks: TimelineMarks,
  months: TimelineMonth[],
): string[] {
  const { practice, interviewing, jobBy } = marks
  const parts: string[] = []
  if (practice) parts.push(`Mostly prep ${span(months, practice)}`)
  if (interviewing) parts.push(`interviewing ${span(months, interviewing)}`)
  const lines = parts.length > 0 ? [`${parts.join(', ')}.`] : []
  if (jobBy)
    lines.push(
      jobBy.from === jobBy.to
        ? `You need an offer by ${months[jobBy.to].name}.`
        : `You would love an offer by ${months[jobBy.from].name} and need one by ${months[jobBy.to].name}.`,
    )
  if (practice && interviewing && jobBy)
    lines.push('None of this has to be tidy. Most searches mix all three.')
  return lines
}

/**
 * What the board says under the line: what has been marked so far, and then —
 * always, as the last line — what the next tap will do.
 *
 * That last line is about `state.stretch` and nothing else. The stretch in
 * hand is what the fences are set from and what a tap actually writes, and the
 * picker can put any of the three in hand at any time. Re-deriving "what
 * next" from the marks instead would let the board say the next tap marks prep
 * while it is in fact about to redraw the offer window.
 */
export function timelineGuidance(
  state: TimelineState,
  months: TimelineMonth[],
): string[] {
  const { stretch, marks, pending } = state
  if (pending !== null) return [PENDING_ASK[stretch](months[pending].name)]
  return [
    ...progressLines(marks, months),
    marks[stretch] ? REDRAW_ASK[stretch] : OPENING_ASK[stretch],
  ]
}

/**
 * The one thing worth raising about the SHAPE of a legal plan.
 *
 * Not a refusal — a squeezed timeline is a real answer, and plenty of people
 * pull it off. It is said once, under the line, next to the thing that caused
 * it, rather than as a chip buried three scrolls away.
 */
export function timelineTightness(
  marks: TimelineMarks,
): { heading: string; body: string } | null {
  const squeezed = (s: TimelineSpan | undefined) =>
    !!s && s.to - s.from + 1 < MIN_PROGRAM_MONTHS
  if (!squeezed(marks.practice) && !squeezed(marks.interviewing)) return null
  return {
    heading: 'This timeline is pretty tight',
    body: 'There are dozens of things to study during interview prep, and it is hard to cover all of them well in this little time. Most people find the sweet spot is two to four months, and the more ambitious your target companies are, the more prep you need.',
  }
}

/**
 * The marks as the month keys that get stored — and nothing else.
 *
 * Not exported: written back on its own, this erases every stored stretch the
 * line has outgrown. {@link storeMarks} is the write.
 */
function toStoredTimeline(
  marks: TimelineMarks,
  months: TimelineMonth[],
): TimelineValue {
  const stored: TimelineValue = {}
  for (const stretch of TIMELINE_STRETCHES) {
    const s = marks[stretch]
    if (!s) continue
    stored[stretch] = { from: months[s.from].key, to: months[s.to].key }
  }
  return stored
}

/**
 * Where a stored stretch lands on one line: the positions it is drawn at, or
 * why it is left off.
 *
 * Decided by {@link placeStretch} and nowhere else. The drawing, the notice
 * asking for a stretch again, the write that keeps one and `ready` all read
 * that answer, so they cannot disagree about a stretch, and a reason added
 * here does not compile until {@link whyOutgrown} says it.
 */
type Placement =
  | { kind: 'drawn'; at: TimelineSpan }
  /** `month` is the earlier end: the one that has passed. */
  | { kind: 'passed'; month: MonthKey }
  /** `latest` is the last month this line lets prep begin in. */
  | { kind: 'startsTooLate'; latest: TimelineMonth }
  | { kind: 'pastEnd' }

/** Each reason a stored stretch is left off the line. */
type Outgrown = Exclude<Placement, { kind: 'drawn' }>

/**
 * A stored stretch onto a line.
 *
 * A stretch with either end off the line is left off the drawing rather than
 * clamped. Quietly sliding somebody's answer onto the line would change what
 * they told us, and drawing the half that is left would invent a start or an
 * end they never chose. It stays in the stored answer, and
 * {@link planOnLine} asks for it again.
 *
 * So is prep starting later than this line lets prep start. A plan drawn on a
 * line that began a month later can hold one — a clock that was ahead, a
 * timezone across the month boundary — and drawn here it would leave the
 * offer window no month to open in.
 *
 * Which month has passed is read off the months, not off which end was
 * stored first. No fence lets a stretch end before it begins, but a
 * hand-written row can hold one, and read by the end stored first it was told
 * it ran past the end of the line.
 */
function placeStretch(
  stretch: TimelineStretch,
  s: StoredSpan,
  months: TimelineMonth[],
): Placement {
  const earlier = monthOrder(s.to) < monthOrder(s.from) ? s.to : s.from
  if (monthOrder(earlier) < monthOrder(months[0].key))
    return { kind: 'passed', month: earlier }
  // Before the far end, so prep starting past it is told the month it has to
  // begin by, not only that it is too far out.
  if (stretch === 'practice') {
    const latest = months[stretchCeilIndex(stretch, months, null)]
    if (monthOrder(s.from) > monthOrder(latest.key))
      return { kind: 'startsTooLate', latest }
  }
  const from = months.findIndex((m) => m.key === s.from)
  const to = months.findIndex((m) => m.key === s.to)
  if (from === -1 || to === -1) return { kind: 'pastEnd' }
  return { kind: 'drawn', at: { from, to } }
}

/** A stored stretch, with where it lands on the line. */
interface PlacedStretch {
  stretch: TimelineStretch
  stored: StoredSpan
  placement: Placement
}

/** Every stretch a stored plan holds, placed on a line, in checklist order. */
function placePlan(
  value: TimelineValue,
  months: TimelineMonth[],
): PlacedStretch[] {
  const placed: PlacedStretch[] = []
  for (const stretch of TIMELINE_STRETCHES) {
    const stored = value[stretch]
    if (stored)
      placed.push({
        stretch,
        stored,
        placement: placeStretch(stretch, stored, months),
      })
  }
  return placed
}

/**
 * A stored answer in the shape the widget stores: the stretches that read as
 * months, in checklist order. Also the one place the pre-widget chip string
 * becomes an empty plan. Idempotent.
 *
 * Never brought into agreement with the line, so it needs no date. A stretch
 * whose months have left the line is exactly what `WidgetDescriptor` says
 * settling must keep.
 */
export function settleTimeline(value: unknown): TimelineValue {
  return readTimeline(value) ?? {}
}

/** The stretches a stored plan holds that this line cannot draw. */
function outgrownStretches(
  value: TimelineValue,
  months: TimelineMonth[],
): TimelineStretch[] {
  return placePlan(value, months)
    .filter(({ placement }) => placement.kind !== 'drawn')
    .map(({ stretch }) => stretch)
}

/**
 * Why a stored stretch is not on the line. "Has passed" only when it has: a
 * stretch running past the far end — a seed, a clock set wrong — is told so
 * instead, and prep starting too late is told the month it has to start by.
 */
function whyOutgrown(
  s: StoredSpan,
  outgrown: Outgrown,
  months: TimelineMonth[],
): string {
  switch (outgrown.kind) {
    case 'passed':
      return s.from === s.to
        ? 'which has passed'
        : `and ${monthName(outgrown.month)} has passed`
    case 'startsTooLate':
      return `but it has to begin by ${outgrown.latest.name} for the whole plan to fit within the next ${months.length} months`
    case 'pastEnd':
      return 'past the end of this line'
    default:
      return assertNever(outgrown, 'Unexpected timeline placement')
  }
}

/**
 * The sentence asking for an outgrown stretch again: what they marked, why
 * it is not drawn, and that it has to be marked again.
 *
 * Said rather than silently cleared. A stretch that is simply not there any
 * more reads as their answer never having saved.
 */
function outgrownNotice(
  stretch: TimelineStretch,
  s: StoredSpan,
  outgrown: Outgrown,
  months: TimelineMonth[],
): string {
  const marked =
    s.from === s.to
      ? monthName(s.from)
      : `${monthName(s.from)} to ${monthName(s.to)}`
  return `${STRETCH_LABELS[stretch]}: you marked ${marked}, ${whyOutgrown(s, outgrown, months)}. Mark it again.`
}

/** A stored plan as one particular line draws it. */
export interface PlanOnLine {
  /** The stretches this line can draw, as positions on it. */
  marks: TimelineMarks
  /** A sentence for each stored stretch it cannot, asking for it again. */
  notices: string[]
  /**
   * Whether there is anything to clear. Read off the stored plan, not the
   * drawing: a stretch the line has outgrown is not drawn, but it is still an
   * answer, and the reset has to be there for it.
   */
  clearable: boolean
}

/**
 * A stored plan onto a line: what to draw, what to say about the stretches
 * that cannot be drawn, and whether there is anything to clear.
 *
 * The widget's one read. An edit on the line writes through
 * {@link storeMarks}, which keeps what this leaves off the line; the only
 * other write is clearing the plan, which is what `clearable` offers. So an
 * outgrown stretch goes away only when it is marked again or the plan is
 * cleared.
 */
export function planOnLine(
  value: TimelineValue,
  months: TimelineMonth[],
): PlanOnLine {
  const marks: TimelineMarks = {}
  const notices: string[] = []
  for (const { stretch, stored, placement } of placePlan(value, months)) {
    if (placement.kind === 'drawn') marks[stretch] = placement.at
    else notices.push(outgrownNotice(stretch, stored, placement, months))
  }
  return {
    marks,
    notices,
    clearable: TIMELINE_STRETCHES.some((stretch) => !!value[stretch]),
  }
}

/**
 * The stored answer after an edit on the line: every stretch now drawn, plus
 * each outgrown stretch the edit did not replace.
 *
 * Writing back only what is drawn would erase an outgrown stretch on the
 * first tap anywhere else, and its notice with it, before they had marked it
 * again.
 */
export function storeMarks(
  value: TimelineValue,
  marks: TimelineMarks,
  months: TimelineMonth[],
): TimelineValue {
  const drawn = toStoredTimeline(marks, months)
  const kept = outgrownStretches(value, months)
  const stored: TimelineValue = {}
  for (const stretch of TIMELINE_STRETCHES) {
    const span =
      drawn[stretch] ?? (kept.includes(stretch) ? value[stretch] : undefined)
    if (span) stored[stretch] = span
  }
  return stored
}

function isStoredSpan(value: unknown): value is StoredSpan {
  // No assertion: this is the guard the jsonb column's `$type` claim rests
  // on, so it should carry no unverified claims of its own. `in` narrows.
  if (typeof value !== 'object' || value === null) return false
  if (!('from' in value) || !('to' in value)) return false
  const span = value
  // Both ends have to be keys `monthOrder` can place, not merely strings. The
  // jsonb column is a `$type` claim and nothing re-validates it on read, so a
  // row written by anything but `stepSchema` — a seed, a backfill — can hold
  // `{ from: '2026' }`. As a bare string check that passed here, and then
  // `monthName` printed `undefined 2026` on the admin queue while every fence
  // comparison against its `NaN` order quietly answered false.
  return (
    typeof span.from === 'string' &&
    typeof span.to === 'string' &&
    isMonthKey(span.from) &&
    isMonthKey(span.to)
  )
}

/**
 * A stored answer read one stretch at a time, or null when it is not a
 * timeline at all.
 *
 * The only way a stored answer is read, whoever is reading it. The question
 * used to be a chip group, so rows written before this widget hold a plain
 * string like `'Within 3 months'`, which is not a timeline. And a seed or a
 * backfill can write one stretch badly: read as a whole, that one stretch
 * took the other two down with it — the admin recap printed nothing and
 * settling wiped the plan. Here a bad stretch costs only itself: it is left
 * out, the same as a stretch never marked. Keys that are not stretches are
 * read past.
 *
 * Not exported. Outside this module the read is {@link settleTimeline}, which
 * takes "not a timeline" as an empty plan, and the descriptor's `is`, which
 * says whether it was one.
 */
function readTimeline(value: unknown): TimelineValue | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value))
    return null
  // `unknown`, not the `any` that `Object.entries` hands back for an object:
  // this is where unchecked jsonb becomes a plan, so nothing may be read off a
  // stretch before `isStoredSpan` has vouched for it.
  const stored = new Map<string, unknown>(Object.entries(value))
  const plan: TimelineValue = {}
  for (const stretch of TIMELINE_STRETCHES) {
    const span = stored.get(stretch)
    if (isStoredSpan(span)) plan[stretch] = { from: span.from, to: span.to }
  }
  return plan
}

/** Whether all three stretches have been marked. */
export function isTimelineComplete(value: TimelineValue): boolean {
  return TIMELINE_STRETCHES.every((stretch) => !!value[stretch])
}

/**
 * Why a stored plan is impossible, or null when it is fine.
 *
 * Shared by the server so a crafted payload cannot store a plan the widget
 * would never have drawn. Stated in month keys alone, so it never consults
 * today's date — otherwise a plan we accepted in September would start being
 * rejected in October, on a row nobody touched.
 *
 * The one thing that leaves unchecked is how far from today a plan starts.
 * How long it runs is a length, not a date, so that is fenced: no longer than
 * the line, which no drawn plan can be.
 *
 * A half-drawn plan passes: incomplete is not the same as illegal, and
 * requiredness is `stepSchema`'s job.
 */
export function timelineOrderProblem(value: TimelineValue): string | null {
  const orders: OrderMarks = {}
  let first = Number.POSITIVE_INFINITY
  let last = Number.NEGATIVE_INFINITY
  for (const stretch of TIMELINE_STRETCHES) {
    const s = value[stretch]
    if (!s) continue
    const order = { from: monthOrder(s.from), to: monthOrder(s.to) }
    orders[stretch] = order
    first = Math.min(first, order.from)
    last = Math.max(last, order.to)
  }
  for (const stretch of TIMELINE_STRETCHES) {
    const s = orders[stretch]
    if (!s) continue
    const late = bindingProblem(endBounds(stretch, orders, s.from), s.to)
    if (late) return late
    const early = bindingProblem(startBounds(stretch, orders), s.from)
    if (early) return early
  }
  // After the fences, so a plan that is out of order is told that first.
  if (last - first + 1 > MONTHS_ON_LINE)
    return `The whole plan has to fit within ${MONTHS_ON_LINE} months, from the first month marked to the last.`
  return null
}

/** Each stretch as the one-line summary says it. */
const SUMMARY_PARTS: Record<TimelineStretch, (s: StoredSpan) => string> = {
  practice: (s) => `Prep ${monthName(s.from)}–${monthName(s.to)}`,
  interviewing: (s) => `interviewing ${monthName(s.from)}–${monthName(s.to)}`,
  jobBy: (s) => `offer by ${monthName(s.to)}`,
}

/**
 * A stored plan on one line, for the admin surfaces and the applicant's own
 * recap. Says only what reads as months, so a half-finished row renders what
 * it has instead of a row of dashes, and a stretch stored badly is left out
 * rather than blanking the stretches beside it.
 */
export function timelineSummary(value: unknown): string {
  const plan = readTimeline(value)
  if (!plan) return ''
  const parts: string[] = []
  for (const stretch of TIMELINE_STRETCHES) {
    const span = plan[stretch]
    if (span) parts.push(SUMMARY_PARTS[stretch](span))
  }
  return parts.join(' · ')
}

/**
 * The one part of a stored plan a reviewer scanning on level needs: the
 * deadline, or, on a plan not drawn that far, the latest stretch it has
 * (LG-1052). The queue's Level lens line quotes this, through the widget's
 * `lensText`; the full {@link timelineSummary} is for the detail page, where
 * there is room for the prep and interviewing windows. Same tolerance as the
 * summary — a stretch stored badly is passed over, not allowed to blank the
 * line.
 */
export function timelineLensText(value: unknown): string {
  const plan = readTimeline(value)
  if (!plan) return ''
  for (const stretch of [...TIMELINE_STRETCHES].reverse()) {
    const span = plan[stretch]
    if (span) return SUMMARY_PARTS[stretch](span)
  }
  return ''
}

/**
 * A month key as stored: `'2026-09'`.
 *
 * `custom` rather than a refined string, because a refined string narrows
 * only its output and still takes any string in. The input is what
 * `saveStep`'s callers are typed against, and `StoredSchema` holds it to
 * `AuditionAnswer`, which says month key.
 */
const monthKeySchema = z.custom<MonthKey>(
  (key) => typeof key === 'string' && isMonthKey(key),
  'not a month',
)

const storedSpanSchema = z.object({
  from: monthKeySchema,
  to: monthKeySchema,
})

/**
 * The three stretches as they are stored.
 *
 * Written as literal keys rather than built from `TIMELINE_STRETCHES`, because
 * `Object.fromEntries` erases them: the schema's output would be
 * `Record<string, StoredSpan | undefined>`, every consumer would need an
 * `as TimelineValue` to get back what it already had, and a renamed or added
 * stretch would keep compiling with no fence of its own. The `satisfies` is
 * what turns that into a compile error here instead.
 */
export const timelineValueSchema = z
  .object({
    practice: storedSpanSchema.optional(),
    interviewing: storedSpanSchema.optional(),
    jobBy: storedSpanSchema.optional(),
  } satisfies Record<TimelineStretch, z.ZodOptional<typeof storedSpanSchema>>)
  .strict()

/**
 * The timeline as it is accepted.
 *
 * `timelineOrderProblem` is the same function the widget's fences are built
 * from, so a plan the widget would refuse to draw is also a plan the server
 * refuses to store — a hand-rolled payload cannot smuggle in an offer that
 * lands before the interviews.
 */
const timelineSchema = timelineValueSchema.superRefine((value, ctx) => {
  const problem = timelineOrderProblem(value)
  if (problem) ctx.addIssue({ code: z.ZodIssueCode.custom, message: problem })
})

/** Everything the rest of the wizard needs to know about this widget. */
export const timelineWidget: WidgetDescriptor = {
  empty: () => ({}),
  // The forgiving read: a plan with one stretch stored badly is still a plan,
  // and answering false here sends it down the legacy-string branch, which
  // prints nothing for an object.
  is: (value) => readTimeline(value) !== null,
  settle: (value) => settleTimeline(value),
  storedSchema: (field) =>
    field.required
      ? // Required means all three stretches, not merely "an object arrived":
        // an empty plan parses fine and is exactly what an untouched widget
        // sends.
        timelineSchema.refine(
          (v) => isTimelineComplete(v),
          'Mark all three stretches.',
        )
      : // An optional timeline normalizes a missing key to an empty plan, the
        // same way an optional multi field normalizes to [], so the stored
        // shape never varies by whether the applicant touched it.
        timelineSchema
          .optional()
          .transform((v) => v ?? {}),
  // Against the line the widget draws, built the same way from the same date
  // (`StepContext.today` says why that matters).
  ready: (value, ctx) =>
    outgrownStretches(settleTimeline(value), timelineMonths(ctx.today))
      .length === 0,
  summary: (value) => {
    const summary = timelineSummary(value)
    return summary ? [summary] : []
  },
  lensText: (value) => timelineLensText(value),
  list: () => false,
  prose: false,
}
